import AppleAppStoreLogo from '~/assets/img/apple-app-store.svg';
import useRegion from '~/hooks/useRegion';
import IconGallery from '~/components/IconGallery';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';
import type { FooterItem } from '~/legacy/types';
import { replaceAll, siteConfig } from '~/legacy/utils';

import FooterAddress from './FooterAddress';
import FooterNav from './FooterNav';

const today = new Date();

const LINKS = {
  DE: [
    {
      link: '/terms-and-conditions/',
      name: 'AGB'
    },
    {
      external: true,
      link: 'https://app.privasee.io/privacy-portal/64ac2c059f43880013f2faf0',
      name: 'Datenschutz'
    },
    { link: '/about/', name: 'Über uns' }
  ],
  GB: [
    {
      link: '/terms-and-conditions/',
      name: 'Terms'
    },
    { link: '/terms-and-conditions/sellers/', name: 'Seller Terms' },
    {
      external: true,
      link: 'https://app.privasee.io/privacy-portal/64ac2c059f43880013f2faf0',
      name: 'Privacy'
    },
    { link: '/about/', name: 'About' },
    { link: '/contact/', name: 'Contact' },
    { link: '/about/', name: 'Editorial Policy' }
  ]
};

export function getMenu(regionCode: string): FooterItem[] {
  return LINKS[regionCode as keyof typeof LINKS] ?? LINKS.GB;
}

const Footer = () => {
  const { code } = useRegion();
  const fireSocialLink = useNavigationLinkInteraction('footer_social');

  const {
    socialMediaLinks: { sneaker: sneakerSocialMediaLinks }
  } = siteConfig;

  const {
    facebook: facebookUrl,
    instagram: instagramUrl,
    tiktok: tiktokUrl,
    youtube: youtubeUrl
  } = sneakerSocialMediaLinks || {};

  const copyRightText = `© ${today.getUTCFullYear()} Sole Supplier® . All rights Reserved®  Company Reg No. 09098756`;

  return (
    <footer className="global-footer ss-theme--secondary" role="contentinfo">
      <div className="global-container site-container content-width">
        <IconGallery
          icons={['instagram', 'tiktok', 'facebook', 'youtube']}
          hrefs={[instagramUrl, tiktokUrl, facebookUrl, youtubeUrl]}
          fillColour="#B7B7B8"
          onLinkClick={fireSocialLink}
        />

        <FooterNav menu={getMenu(code)} />

        <FooterAddress />

        <div className="global-footer__separator">
          {code === 'GB' && <DownloadApp />}
          <p className="global-footer__copyright">{copyRightText}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

const APP_STORE_BADGE_LABEL = 'Download on the App Store';

function DownloadApp() {
  const { name: appName = '', appleStore: appleStoreUrl = '' } = siteConfig;
  const fireAppLink = useNavigationLinkInteraction('footer_app');

  const mapWith = {
    '{{appName}}': appName
  };

  const appStoreText = replaceAll(
    'Get the latest releases, dates, news and your favourite brands on {{appName}} app.',
    mapWith
  );

  return (
    <div className="global-footer__appstorewrapper  flex--center flex--start">
      <a
        className="app-store"
        href={appleStoreUrl}
        onClick={() =>
          fireAppLink(appleStoreUrl, 1, { label: APP_STORE_BADGE_LABEL })
        }
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          alt={APP_STORE_BADGE_LABEL}
          src={AppleAppStoreLogo}
          loading="lazy"
        />
      </a>

      <p className="global-footer__appstoretext">{appStoreText}</p>
    </div>
  );
}
